import { Edit, HelpOutline } from '@mui/icons-material';
import { IconButton, Modal, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import './DataSleep.scss';
import { useSleepTrend } from '../../../../hooks/useSleepTrend';
import {
  dataTrendPeriodAtom,
  selectedDateAtom,
  useAuthContext,
  useDiagnoseChronotype,
} from '../../../../data-access';
import { CustomTooltip, CustomTooltipContent, TextLoader } from '../../../common';

import { chronotypeTitleSet, EditSleepModalView } from '../../../KanbanView';
import { LoadingButton } from '@mui/lab';
import { dayjs, trackEventMixpanel } from '../../../../utils';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import { SleepBarAndInfo, SleepBarChart } from '../../DataSleepDashboard';
import { chronotypeTooltipInfo, DataViewMenuItems } from '../../constants';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import { formatDurationHoursMinutes } from '../../i18n';

const DataSleep = () => {
  const { t } = useTranslation();
  const {
    targetSleepStage,
    targetSleepHours,
    targetAwakeTime,
    isLoading,
    targetSleepTime,
    hasNoSleepStage,
    dataByDate,
    heartRateSummary,
    hasNoSleepData,
  } = useSleepTrend();
  const selectedDate = useRecoilValue(selectedDateAtom);
  const {
    diagnoseChronotype: diagnoseChronotypeMutation,
    error,
    isPending: isDiagnosing,
  } = useDiagnoseChronotype();
  const dataTrendPeriod = useRecoilValue(dataTrendPeriodAtom);
  const isWeekly = dataTrendPeriod === 'weekly';
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { user } = useAuthContext();
  const explanation = DataViewMenuItems.find((item) => item.id === 'dataSleep')?.explanation;
  const localizedExplanation = explanation
    ? {
        ...explanation,
        title: t('data.sleep.tooltipTitle'),
        description: t('data.sleep.tooltipDescription'),
      }
    : undefined;
  const localizedChronotypeTooltipInfo = {
    ...chronotypeTooltipInfo,
    title: t('data.sleep.chronotypeTooltipTitle'),
    description: t('data.sleep.chronotypeTooltipDescription'),
  };
  const hasChronotype = !!user.chronotype;
  const isFutureDate = selectedDate.isAfter(dayjs(), 'day');
  const shouldShowEditButton = !isFutureDate && !isWeekly;
  const diagnoseChronotype = async () => {
    if (!user.metricId || !user.userId) {
      return;
    }

    await diagnoseChronotypeMutation({ userId: user.userId, metricId: user.metricId });
    trackEventMixpanel('diagnose_chronotype');
  };

  return (
    <div className="data-sleep">
      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <EditSleepModalView onClose={() => setIsModalOpen(false)} />
      </Modal>
      <Typography variant="body1" className="data-sleep__title">
        {t('data.sleep.title')}
        <CustomTooltip title={<CustomTooltipContent {...localizedExplanation} />}>
          <HelpOutline fontSize="inherit" width={6} className="data-sleep__title__icon" />
        </CustomTooltip>
      </Typography>
      <div className="data-sleep__content">
        {/* /69px is the height of the duration box */}
        {isLoading ? (
          <Stack height={69} justifyContent="center" alignItems="flex-start">
            <TextLoader size={12} />
          </Stack>
        ) : (
          <div className="data-sleep__duration">
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="h2" className="data-sleep__duration-value">
                {hasNoSleepData
                  ? t('data.common.emptyDuration')
                  : formatDurationHoursMinutes(t, targetSleepHours.hours, targetSleepHours.minutes)}
              </Typography>
              {shouldShowEditButton && (
                <IconButton
                  onClick={() => setIsModalOpen(true)}
                  className="data-sleep__duration__edit"
                  aria-label={t('data.sleep.editSleep')}
                >
                  <Edit className="data-sleep__duration__edit__icon" />
                </IconButton>
              )}
            </Stack>
            <Typography variant="body2" className="data-sleep__duration-label">
              {isWeekly ? t('data.sleep.averageTimeAsleep') : t('data.sleep.timeAsleep')}
            </Typography>
          </div>
        )}
        <div className="data-sleep__box">
          <div className="data-sleep__chronotype">
            <div className="data-sleep__chronotype-header">
              <Typography variant="body1" className="data-sleep__chronotype-title">
                {hasChronotype
                  ? t(`data.sleep.chronotypes.${user.chronotype}`, {
                      defaultValue: chronotypeTitleSet[user.chronotype!],
                    })
                  : t('data.sleep.chronotype')}
              </Typography>
              <CustomTooltip title={<CustomTooltipContent {...localizedChronotypeTooltipInfo} />}>
                <HelpOutlineOutlinedIcon className="data-sleep__chronotype__tooltip-icon" />
              </CustomTooltip>
            </div>
            {!hasChronotype && (
              <>
                <Typography variant="body2" className="data-sleep__chronotype-description">
                  {t('data.sleep.chronotypePrompt')}
                </Typography>
                <LoadingButton
                  disabled={isDiagnosing}
                  loading={isDiagnosing}
                  onClick={diagnoseChronotype}
                  variant="contained"
                  className="data-sleep__chronotype-button"
                >
                  <Typography variant="body2">{t('data.sleep.diagnose')}</Typography>
                </LoadingButton>
                {!!error && (
                  <Typography className="data-sleep__chronotype-error">
                    {t('data.sleep.noSleepData')}
                  </Typography>
                )}
              </>
            )}
          </div>
          <div className="data-sleep__info">
            {isWeekly ? (
              <SleepBarChart
                dataByDate={dataByDate}
                targetSleepStage={targetSleepStage}
                targetSleepHours={targetSleepHours}
                hasNoSleepStage={hasNoSleepStage}
              />
            ) : (
              <SleepBarAndInfo
                targetSleepStage={targetSleepStage}
                targetAwakeTime={targetAwakeTime}
                targetSleepTime={targetSleepTime}
                hasNoSleepStage={hasNoSleepStage}
                heartRateSummary={heartRateSummary}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataSleep;
